/**
 * DataTable — universal data table component.
 *
 * Takes column definitions + data array → renders full data table with:
 *   - Sortable headers with terracotta ↑↓ indicators
 *   - Filter bar: search input + optional status dropdown
 *   - Tab navigation for filtered views
 *   - Pagination footer with page numbers
 *   - Empty state + skeleton loading
 *   - Alternating row tint (even/odd)
 *   - Row click navigation
 *
 * Cell renderers provided for common patterns:
 *   - TitleCell   (cover thumbnail + title + subtitle)
 *   - AvatarCell  (initials avatar + primary + secondary text)
 *   - BadgeCell   (StatusBadge)
 *   - DateCell    (Intl.DateTimeFormat)
 *   - ActionsCell (view/edit/delete buttons)
 *
 * Design spec: Scholarly Modernist — warm off-white paper, terracotta gold.
 *
 * @layer-1 — use anywhere a table is needed
 *
 * Usage:
 * ```tsx
 * const columns: DataTableColumnDef<Title>[] = [
 *   {
 *     key: 'title',
 *     header: 'Title',
 *     sortable: true,
 *     render: (row) => <TitleCell title={row.title} subtitle={row.isbn13} />,
 *   },
 *   { key: 'author', header: 'Author' },
 *   {
 *     key: 'status',
 *     header: 'Status',
 *     render: (row) => <BadgeCell status={row.status} />,
 *   },
 *   {
 *     key: 'actions',
 *     header: '',
 *     render: (row) => <ActionsCell onView={() => navigate(`/titles/${row.id}`)} />,
 *   },
 * ];
 *
 * <DataTable columns={columns} data={titles} pageSize={10} />
 * ```
 */
import { useTranslation } from "react-i18next";
import { Eye, Pencil, Trash, Check, X, Pause, RefreshCw, Power, PowerOff } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { usePageDirection } from "@/shared/hooks/usePageDirection";
import { useLocale } from "@/shared/hooks/useLocale";
import { getEffectiveDir } from "@/shared/lib/direction";
import {
  DataTableWrapper,
  ThemedTable,
  ThemedTh,
  ThemedTd,
  ThemedTr,
  DataTableFooter,
  DataTableActionButton,
  type DataTableActionVariant,
} from "./ThemedTable";
import { StatusBadge, type BadgeStatus } from "./StatusBadge";
import { FilterSearchInput } from "./FilterSearchInput";
import { CoverArt } from "./CoverArt";
import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { useState, useMemo, useCallback, useRef, useEffect } from "react";

/* ── Types ──────────────────────────────────────────────────────────────────── */

export type { BadgeStatus };

/**
 * Column definition for DataTable.
 * `key` is a unique identifier for the column (used as React key).
 * `header` is the column header text (i18n key or raw string).
 * `sortable` adds sort indicator + click handler.
 * `render` customizes the cell — receives full row object + row index.
 * `width` is CSS width string (e.g. '42%', 'w-48') — used before first resize drag.
 * `align` controls text alignment. Default: 'start'.
 * `flexible` marks this column as the auto-width column that absorbs leftover space.
 *   Only one column per table should be marked flexible (typically the title/name col).
 *   If none marked, the first column is treated as flexible.
 * `minWidth` is the minimum px width enforced by the column resizer.
 */
export interface DataTableColumnDef<T> {
  key: string;
  header: string;
  sortable?: boolean;
  width?: string;
  align?: "start" | "end" | "center";
  flexible?: boolean;
  minWidth?: number;
  /** Return ReactNode to render in this cell. Receives full row data. */
  render?: (row: T, rowIndex: number) => ReactNode;
}

// Generic row constraint. `unknown` values would reject named interfaces
// (e.g. AdminTitle) since TS doesn't give them an implicit index signature,
// so `any` is the contained, intentional choice for this generic table.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyRecord = Record<string, any>;

/* ── useDataTable hook ─────────────────────────────────────────────────────── */

/**
 * Manages search, filter, sort, pagination for a dataset.
 * Returns filtered+sorted+paged data + state setters.
 *
 * @example
 * const { search, setSearch, filter, setFilter, page, setPage,
 *         pageSize, total, totalPages, filtered } = useDataTable(data, {
 *   searchKeys: ['title', 'author'],
 *   filterKey: 'status',
 *   pageSize: 10,
 * });
 */
export function useDataTable<T extends AnyRecord>(
  data: T[],
  options: {
    searchKeys?: string[];
    filterKey?: string;
    filterOptions?: { value: string; label: string }[];
    pageSize?: number;
  }
) {
  const { searchKeys = [], filterKey, pageSize: _pageSize = 10 } = options;

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("");
  const [sortKey, setSortKey] = useState<string>("");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    let result = [...data];

    if (search.trim() && searchKeys.length > 0) {
      const q = search.trim().toLowerCase();
      result = result.filter((row) =>
        searchKeys.some((k) => {
          const val = row[k];
          return String(val).toLowerCase().includes(q);
        })
      );
    }

    if (filter && filterKey) {
      result = result.filter((row) => String(row[filterKey]) === filter);
    }

    if (sortKey) {
      result.sort((a, b) => {
        const aVal = a[sortKey];
        const bVal = b[sortKey];
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
        return sortDir === "asc" ? cmp : -cmp;
      });
    }

    return result;
  }, [data, search, filter, sortKey, sortDir, searchKeys, filterKey]);

  const pageSize = _pageSize;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const handleSearchChange = useCallback((s: string) => {
    setSearch(s);
    setPage(1);
  }, []);

  const handleFilterChange = useCallback((f: string) => {
    setFilter(f);
    setPage(1);
  }, []);

  const handleSort = useCallback(
    (key: string) => {
      /* 3-state cycle: unsorted → asc → desc → unsorted */
      if (sortKey === key) {
        if (sortDir === "asc") {
          setSortDir("desc");
        } else {
          setSortKey("");
          setSortDir("asc");
        }
      } else {
        setSortKey(key);
        setSortDir("asc");
      }
      setPage(1);
    },
    [sortKey, sortDir]
  );

  return {
    search,
    setSearch: handleSearchChange,
    filter,
    setFilter: handleFilterChange,
    sortKey,
    setSortKey: handleSort,
    sortDir,
    page,
    setPage,
    pageSize,
    total: filtered.length,
    totalPages,
    filtered: paged,
    allFiltered: filtered,
  };
}

/* ── useColResize ──────────────────────────────────────────────────────── */

/**
 * Manages column resize drag state.
 *
 * Mirrors the design-spec prototype behavior:
 *   - First drag freezes all NON-flex columns to their rendered px widths.
 *     The flex column (flexColIdx) stays `width: auto` so it absorbs leftover space.
 *   - Per-column minWidth prevents content clipping/overlap when dragging narrow.
 *   - Table width = 100% while flex col can fill remaining space.
 *     If pinned cols + flex col min exceed the scroll container, table grows past
 *     100% and the container scrolls horizontally.
 */
function useColResize(
  tableRef: React.RefObject<HTMLTableElement | null>,
  flexColIdx: number,
  colMins: number[],
) {
  // colWidthsRef[i] = px width; 0 means "auto" (flex col, stays unconstrained)
  const colWidthsRef = useRef<number[]>([]);
  const frozenRef = useRef(false);
  const [colWidths, setColWidths] = useState<number[]>([]);
  const [tableStyle, setTableStyle] = useState<React.CSSProperties | undefined>(undefined);
  const [draggingCol, setDraggingCol] = useState<number | null>(null);

  // Keep config in refs so event listeners always see current values. Sync in
  // an effect (not during render) — writing ref.current during render is flagged
  // by the React Compiler and the pointer listeners only read these post-commit.
  const flexColIdxRef = useRef(flexColIdx);
  const colMinsRef = useRef(colMins);
  useEffect(() => {
    flexColIdxRef.current = flexColIdx;
    colMinsRef.current = colMins;
  });

  const handleResizeStart = useCallback(
    (colIdx: number, e: React.PointerEvent<HTMLSpanElement>) => {
      e.preventDefault();
      e.stopPropagation();

      const FLEX_IDX = flexColIdxRef.current;
      const FLEX_MIN = colMinsRef.current[FLEX_IDX] ?? 220;

      /* First drag: freeze non-flex cols to px; leave flex col as auto (0). */
      if (!frozenRef.current && tableRef.current) {
        const ths = Array.from(
          tableRef.current.querySelectorAll(":scope > thead > tr > th")
        );
        const widths = ths.map((th, i) =>
          i === FLEX_IDX ? 0 : th.getBoundingClientRect().width
        );
        colWidthsRef.current = widths;
        frozenRef.current = true;
        setColWidths([...widths]);
        setTableStyle({ width: "100%", minWidth: 0 });
      }

      const isFlexCol = colIdx === FLEX_IDX;

      /* If user grabs the flex col resizer for the first time, pin its rendered width. */
      if (isFlexCol && colWidthsRef.current[colIdx] === 0 && tableRef.current) {
        const th = tableRef.current.querySelectorAll(":scope > thead > tr > th")[colIdx];
        if (th) colWidthsRef.current[colIdx] = th.getBoundingClientRect().width;
      }

      const startX = e.clientX;
      const startColW = colWidthsRef.current[colIdx] ?? 100;
      const scrollEl = tableRef.current?.closest(".overflow-x-auto") as HTMLElement | null;

      setDraggingCol(colIdx);
      const target = e.currentTarget;
      target.setPointerCapture(e.pointerId);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      /* Sum of all pinned (non-flex) columns, optionally skipping one index. */
      function pinnedSum(skip: number) {
        return colWidthsRef.current.reduce((s, w, i) => {
          if (i === FLEX_IDX || i === skip) return s;
          return s + (w || 0);
        }, 0);
      }

      function onMove(ev: PointerEvent) {
        const dx = ev.clientX - startX;
        const minW = colMinsRef.current[colIdx] ?? 80;
        const nextW = Math.max(minW, startColW + dx);
        colWidthsRef.current[colIdx] = nextW;
        setColWidths([...colWidthsRef.current]);

        /* Decide: table fits in wrap (flex col absorbs) vs needs horizontal scroll. */
        const wrapW = scrollEl?.clientWidth ?? 0;
        const needed =
          pinnedSum(isFlexCol ? -1 : colIdx) +
          nextW +
          (isFlexCol ? 0 : FLEX_MIN);

        if (wrapW > 0 && needed > wrapW) {
          setTableStyle({ width: `${needed}px`, minWidth: 0 });
        } else {
          setTableStyle({ width: "100%", minWidth: 0 });
        }
      }

      function onUp() {
        target.removeEventListener("pointermove", onMove);
        target.removeEventListener("pointerup", onUp);
        target.removeEventListener("pointercancel", onUp);
        setDraggingCol(null);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      }

      target.addEventListener("pointermove", onMove);
      target.addEventListener("pointerup", onUp);
      target.addEventListener("pointercancel", onUp);
    },
    [tableRef]
  );

  return { colWidths, tableStyle, draggingCol, handleResizeStart };
}

/* ── DataTable ─────────────────────────────────────────────────────────── */

interface DataTableProps<T extends AnyRecord> {
  /** Column definitions. */
  columns: DataTableColumnDef<T>[];
  /** Row data. */
  data: T[];
  /** Current page number (1-based). */
  page?: number;
  /** Total pages. */
  totalPages?: number;
  /** Called when page changes. */
  onPageChange?: (page: number) => void;
  /** Footer info string — e.g. "5 of 34 titles". */
  footerInfo?: string;
  /** Row click handler. */
  onRowClick?: (row: T) => void;
  /** Show pagination footer. Default: true. */
  showPagination?: boolean;
  /** Currently active sort column key — drives the ↑/↓ indicator on headers. */
  sortKey?: string;
  /** Current sort direction. */
  sortDir?: "asc" | "desc";
  /** Called when a sortable header is clicked (cycles asc → desc → null). */
  onSortChange?: (key: string) => void;
  className?: string;
}

/**
 * Universal DataTable.
 *
 * Accepts DataTableColumnDef[] + data array.
 * Renders the full table card: wrapper, headers, body, footer with pagination.
 */
export function DataTable<T extends AnyRecord>({
  columns,
  data,
  page = 1,
  totalPages = 1,
  onPageChange,
  footerInfo,
  onRowClick,
  showPagination = true,
  sortKey: externalSortKey,
  sortDir: externalSortDir,
  onSortChange,
  className,
}: DataTableProps<T>) {
  const { t } = useTranslation();
  const dir = usePageDirection();
  const tableRef = useRef<HTMLTableElement>(null);

  /* Derive flex col index and per-column minimums from column defs. */
  const flexColIdx = useMemo(() => {
    const idx = columns.findIndex((c) => c.flexible);
    return idx >= 0 ? idx : 0;
  }, [columns]);
  const colMins = useMemo(() => columns.map((c) => c.minWidth ?? 80), [columns]);

  /* Adaptive floor: the table never shrinks below the sum of every column's
     minimum. Above this width the overflow-x-auto container has slack, so
     table-fixed squeezes columns to fit (no scroll); below it the container
     scrolls. The container IS the content area (the fixed sidebar is already
     excluded), so desktop never scrolls as long as the floor stays within the
     desktop content width (~720px at the 1024px breakpoint). */
  const minTableWidth = useMemo(
    () => colMins.reduce((sum, w) => sum + w, 0),
    [colMins],
  );

  const { colWidths, tableStyle, draggingCol, handleResizeStart } =
    useColResize(tableRef, flexColIdx, colMins);

  /* Internal sort — used when caller doesn't wire up onSortChange. */
  const [internalSortKey, setInternalSortKey] = useState("");
  const [internalSortDir, setInternalSortDir] = useState<"asc" | "desc">("asc");

  const isExternalSort = Boolean(onSortChange);
  const sortKey = isExternalSort ? (externalSortKey ?? "") : internalSortKey;
  const sortDir = isExternalSort ? (externalSortDir ?? "asc") : internalSortDir;

  function handleSort(key: string) {
    if (isExternalSort) { onSortChange!(key); return; }
    /* 3-state cycle: unsorted → asc → desc → unsorted */
    if (internalSortKey === key) {
      if (internalSortDir === "asc") {
        setInternalSortDir("desc");
      } else {
        setInternalSortKey("");
        setInternalSortDir("asc");
      }
    } else {
      setInternalSortKey(key);
      setInternalSortDir("asc");
    }
  }

  /* Sort data internally when no external handler is provided. */
  const displayData = useMemo(() => {
    if (isExternalSort || !sortKey) return data;
    return [...data].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: "base" });
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [data, isExternalSort, sortKey, sortDir]);

  /* All columns are sortable by default except the actions column. */
  function isColSortable(col: DataTableColumnDef<T>) {
    return col.sortable !== false && col.key !== "actions";
  }

  return (
    <DataTableWrapper className={className}>
      <div className="overflow-x-auto">
        <ThemedTable
          ref={tableRef}
          style={tableStyle ?? { minWidth: `${minTableWidth}px` }}
        >
          {/* colgroup only after widths are frozen to pixel values.
              Flex column gets width:auto so it absorbs leftover space;
              all other columns get their pinned px widths. */}
          {colWidths.length > 0 && (
            <colgroup>
              {columns.map((col, i) => (
                <col
                  key={col.key}
                  style={
                    i === flexColIdx || colWidths[i] === 0
                      ? { width: "auto" }
                      : colWidths[i] !== undefined
                      ? { width: `${colWidths[i]}px` }
                      : undefined
                  }
                />
              ))}
            </colgroup>
          )}
          <thead>
            <tr>
              {columns.map((col, colIdx) => (
                <ThemedTh
                  key={col.key}
                  sortable={isColSortable(col)}
                  sorted={isColSortable(col) && sortKey === col.key ? sortDir : null}
                  onClick={isColSortable(col) ? () => handleSort(col.key) : undefined}
                  resizable={colIdx < columns.length - 1}
                  isDraggingResizer={draggingCol === colIdx}
                  onResizeStart={
                    colIdx < columns.length - 1
                      ? (e) => handleResizeStart(colIdx, e)
                      : undefined
                  }
                  className={cn(
                    col.align === "center" && "text-center",
                    col.align === "end" && "text-end",
                    col.key === "actions" && "text-start"
                  )}
                  style={
                    colWidths.length === 0
                      ? {
                          width:
                            col.width ??
                            (col.key === "actions" ? "168px" : undefined),
                        }
                      : undefined
                  }
                >
                  {col.key === "actions" && !col.header
                    ? t("common.actions")
                    : col.header}
                </ThemedTh>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="py-16 text-center"
                  dir={dir}
                >
                  <EmptyTableState />
                </td>
              </tr>
            ) : (
              displayData.map((row, rowIdx) => (
                <ThemedTr
                  key={rowIdx}
                  onClick={() => onRowClick?.(row)}
                  className={cn(onRowClick && "cursor-pointer")}
                >
                  {columns.map((col) => (
                    <ThemedTd
                      key={col.key}
                      className={cn(
                        col.align === "center" && "text-center",
                        col.align === "end" && "text-end"
                      )}
                    >
                      {col.render ? (
                        col.render(row, rowIdx)
                      ) : (
                        <span
                          className="block truncate"
                          title={String(row[col.key] ?? "—")}
                        >
                          {String(row[col.key] ?? "—")}
                        </span>
                      )}
                    </ThemedTd>
                  ))}
                </ThemedTr>
              ))
            )}
          </tbody>
        </ThemedTable>
      </div>

      {showPagination && onPageChange && (
        <DataTableFooter
          info={footerInfo}
          page={page}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      )}
    </DataTableWrapper>
  );
}

/* ── Empty state ───────────────────────────────────────────────────────────── */

function EmptyTableState() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-2">
      <svg
        width="32"
        height="32"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-[var(--text-tertiary)]"
      >
        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span className="text-sm text-[var(--text-tertiary)]">
        {t("common.noResults") ?? "No results found"}
      </span>
    </div>
  );
}

/* ── FilterBar ──────────────────────────────────────────────────────────────── */

/**
 * FilterBar — search input + optional status dropdown.
 * Used above tables for filtering by search query + status.
 */
export interface FilterBarProps {
  search: string;
  onSearchChange: (s: string) => void;
  filter?: string;
  onFilterChange?: (f: string) => void;
  filterOptions?: { value: string; label: string }[];
  searchPlaceholder?: string;
  className?: string;
}

export function FilterBar({
  search,
  onSearchChange,
  filter,
  onFilterChange,
  filterOptions,
  searchPlaceholder,
  className,
}: FilterBarProps) {
  const { t } = useTranslation();

  return (
    <div className={cn("flex gap-3 flex-wrap", className)}>
      <FilterSearchInput
        value={search}
        onChange={onSearchChange}
        placeholder={searchPlaceholder ?? t("common.search")}
        className="min-w-48 flex-1"
      />
      {filterOptions && filterOptions.length > 0 && onFilterChange && (
        <select
          value={filter ?? ""}
          onChange={(e) => onFilterChange(e.target.value)}
          className={cn(
            "h-10 px-3 text-[12.5px]",
            "bg-[var(--card)] border border-[var(--btn-secondary-border)]",
            "rounded-[10px] outline-none cursor-pointer",
            "text-[var(--text-primary)]",
            "focus-visible:ring-2 focus-visible:ring-[var(--accent-gold)] focus-visible:ring-offset-1"
          )}
        >
          <option value="">All</option>
          {filterOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

/* ── TabNav ─────────────────────────────────────────────────────────────────── */

/**
 * TabNav — underline-style tab filter.
 * Used for: All | Published | Pending | Draft | Editing
 */
export interface TabDef {
  key: string;
  label: string;
}

export interface TabNavProps {
  tabs: TabDef[];
  active: string;
  onChange: (key: string) => void;
  className?: string;
}

export function TabNav({ tabs, active, onChange, className }: TabNavProps) {
  return (
    <div
      className={cn(
        "relative flex items-end",
        className
      )}
      role="tablist"
      style={{ borderBottom: '1px solid var(--border-default)' }}
    >
      {tabs.map((tab) => {
        const isActive = active === tab.key;
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(tab.key)}
            className={cn(
              "relative py-3 px-3 text-[13.5px] font-semibold cursor-pointer",
              "transition-colors duration-150 hover:text-[var(--text-primary)]",
              "select-none flex items-center gap-1.5",
              isActive
                ? "text-[var(--text-primary)]"
                : "text-[var(--text-tertiary)]"
            )}
          >
            <span className="relative z-10">{tab.label}</span>
            {/* Sliding terracotta underline — animates with scaleX */}
            <span
              className="absolute bottom-0 start-0 end-0 h-0.5 rounded-full"
              style={{
                background: 'var(--accent-gold)',
                transform: isActive ? 'scaleX(1)' : 'scaleX(0)',
                transition: 'transform 220ms cubic-bezier(0.25, 1, 0.5, 1)',
                transformOrigin: 'center',
              }}
            />
          </button>
        );
      })}
    </div>
  );
}

/* ── Cell renderers ────────────────────────────────────────────────────────── */

/**
 * TitleCell — cover thumbnail + title + subtitle (ISBN or author).
 * Design: 38×52px rounded cover, title 14px semibold, subtitle 11px mono.
 */
export function TitleCell({
  title,
  subtitle,
  coverImageUrl,
}: {
  title: string;
  subtitle?: string;
  coverImageUrl?: string;
}) {
  return (
    <div className="flex items-center gap-3 min-w-0">
      {/* Cover thumbnail */}
      <div
        className="w-[38px] h-[52px] rounded-[6px] overflow-hidden shrink-0"
        style={{
          background: "var(--color-surface-high)",
          border: "1px solid var(--border-light)",
          boxShadow: "0 1px 4px rgba(26,25,23,0.10)",
        }}
      >
        <CoverArt url={coverImageUrl} alt={title} iconSize={18} />
      </div>
      {/* Text */}
      <div className="flex flex-col gap-1 min-w-0 flex-1">
        {/* Single line + ellipsis; full value on hover via title attr. */}
        <p
          className="truncate text-[14px] font-semibold leading-tight text-[var(--text-primary)]"
          title={title}
        >
          {title}
        </p>
        {subtitle && (
          <p
            className="truncate text-[11px] text-[var(--text-tertiary)] font-mono"
            title={subtitle}
          >
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * AvatarCell — initials avatar circle + name + optional secondary text.
 * Design: 30×30 circle, terracotta gold bg, initials font-bold.
 */
export function AvatarCell({
  name,
  secondary,
  initials,
}: {
  name: string;
  secondary?: string;
  initials?: string;
}) {
  const chars = initials ?? name?.[0]?.toUpperCase() ?? "?";
  return (
    <div className="flex items-center gap-2.5">
      <div
        className="w-[30px] h-[30px] rounded-full flex items-center justify-center text-[11.5px] font-bold shrink-0"
        style={{
          background: "var(--color-terracotta-tint)",
          border: "1.5px solid var(--color-terracotta-border)",
          color: "var(--color-gold-text)",
        }}
      >
        {chars}
      </div>
      <div className="flex flex-col min-w-0">
        <span
          className="truncate text-[13px] font-medium text-[var(--text-secondary)]"
          title={name}
        >
          {name}
        </span>
        {secondary && (
          <span className="truncate text-[11px] text-[var(--text-tertiary)]" title={secondary}>
            {secondary}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * BadgeCell — wraps StatusBadge for table cell use.
 */
export function BadgeCell({ status }: { status: BadgeStatus }) {
  return <StatusBadge status={status} />;
}

/**
 * DateCell — formatted date in medium style, tabular nums.
 * Uses useLocale so the date's separators follow the language and its digit
 * glyphs follow the user's numberFormat preference (independent of language).
 */
export function DateCell({ date }: { date: string | undefined }) {
  const { fmtDate } = useLocale();
  const parsed = date ? new Date(date) : null;
  return (
    <span
      className="text-[12.5px] tabular-nums text-[var(--text-tertiary)]"
      dir={getEffectiveDir()}
    >
      {parsed ? fmtDate(parsed, { dateStyle: "medium" }) : "—"}
    </span>
  );
}

/**
 * ActionsCell — view / edit / delete icon buttons.
 * Stops click propagation so row click doesn't fire.
 */
export function ActionsCell({
  onView,
  onEdit,
  onDelete,
}: {
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  return (
    <div
      className="flex items-center gap-1"
      onClick={(e) => e.stopPropagation()}
    >
      {onView && (
        <DataTableActionButton onClick={onView} aria-label="View">
          <Eye size={13} strokeWidth={2} />
        </DataTableActionButton>
      )}
      {onEdit && (
        <DataTableActionButton onClick={onEdit} aria-label="Edit">
          <Pencil size={13} strokeWidth={2} />
        </DataTableActionButton>
      )}
      {onDelete && (
        <DataTableActionButton
          variant="danger"
          onClick={onDelete}
          aria-label="Delete"
        >
          <Trash size={13} strokeWidth={2} />
        </DataTableActionButton>
      )}
    </div>
  );
}

/* ── RowActionButton ───────────────────────────────────────────────────────── */

/** Semantic action names that map to icon + hover color automatically. */
export type RowAction =
  | "view"
  | "edit"
  | "delete"
  | "approve"
  | "reject"
  | "suspend"
  | "reactivate"
  | "activate"
  | "deactivate"
  | "review";

const ROW_ACTION_MAP: Record<
  RowAction,
  {
    icon: React.ReactElement;
    variant: DataTableActionVariant;
  }
> = {
  view:       { icon: <Eye size={13} strokeWidth={2} />,       variant: "default" },
  edit:       { icon: <Pencil size={13} strokeWidth={2} />,    variant: "default" },
  delete:     { icon: <Trash size={13} strokeWidth={2} />,     variant: "danger"  },
  approve:    { icon: <Check size={13} strokeWidth={2.5} />,   variant: "success" },
  reactivate: { icon: <RefreshCw size={13} strokeWidth={2} />, variant: "success" },
  reject:     { icon: <X size={13} strokeWidth={2.5} />,       variant: "danger"  },
  suspend:    { icon: <Pause size={13} strokeWidth={2} />,     variant: "warning" },
  activate:   { icon: <Power size={13} strokeWidth={2} />,     variant: "success" },
  deactivate: { icon: <PowerOff size={13} strokeWidth={2} />,  variant: "warning" },
  review:     { icon: <Eye size={13} strokeWidth={2} />,       variant: "default" },
};

interface RowActionButtonProps
  extends Omit<ComponentPropsWithoutRef<"button">, "children"> {
  /** Semantic action — determines icon and hover color. */
  action: RowAction;
}

/**
 * Semantic icon-only table action button.
 *
 * Maps `action` → (Lucide icon + hover-color variant) automatically.
 * Default state: transparent, no color. Hover state: semantic fill.
 *
 * @example
 * <RowActionButton action="approve" onClick={handleApprove} aria-label="Approve" />
 * <RowActionButton action="reject"  onClick={handleReject}  aria-label="Reject"  />
 */
export function RowActionButton({ action, ...props }: RowActionButtonProps) {
  const { icon, variant } = ROW_ACTION_MAP[action];
  return (
    <DataTableActionButton variant={variant} {...props}>
      {icon}
    </DataTableActionButton>
  );
}

/* ── ApprovalActionsCell ───────────────────────────────────────────────────── */

/** Publisher approval workflow states. */
export type ApprovalStatus = "pending" | "approved" | "suspended" | "rejected";

interface ApprovalActionsCellProps {
  status: ApprovalStatus;
  /** Opens the detail view — same as clicking the row. */
  onView?: () => void;
  onApprove?: () => void;
  onReject?: () => void;
  onSuspend?: () => void;
  onReactivate?: () => void;
}

/**
 * Approval workflow action cell — renders icon buttons contextually by status.
 *
 * State machine:
 *   pending   → [👁 view] [✓ approve] [✗ reject]
 *   approved  → [👁 view] [⏸ suspend]
 *   suspended → [👁 view] [↺ reactivate]
 *   rejected  → [👁 view]
 *
 * Stops click propagation so row click doesn't fire.
 */
export function ApprovalActionsCell({
  status,
  onView,
  onApprove,
  onReject,
  onSuspend,
  onReactivate,
}: ApprovalActionsCellProps) {
  const { t } = useTranslation("admin");

  return (
    <div
      className="flex items-center gap-1"
      onClick={(e) => e.stopPropagation()}
    >
      {onView && (
        <RowActionButton
          action="view"
          onClick={onView}
          aria-label={t("publishers.actions.view")}
          title={t("publishers.actions.view")}
        />
      )}
      {status === "pending" && (
        <>
          <RowActionButton
            action="approve"
            onClick={onApprove}
            aria-label={t("publishers.actions.approve")}
            title={t("publishers.actions.approve")}
          />
          <RowActionButton
            action="reject"
            onClick={onReject}
            aria-label={t("publishers.actions.reject")}
            title={t("publishers.actions.reject")}
          />
        </>
      )}
      {status === "approved" && (
        <RowActionButton
          action="suspend"
          onClick={onSuspend}
          aria-label={t("publishers.fields.suspend")}
          title={t("publishers.fields.suspend")}
        />
      )}
      {status === "suspended" && (
        <RowActionButton
          action="reactivate"
          onClick={onReactivate}
          aria-label={t("publishers.actions.activate")}
          title={t("publishers.actions.activate")}
        />
      )}
    </div>
  );
}

/* ── TitleActionsCell ──────────────────────────────────────────────────────── */

/** Local mirror of AdminTitleStatus — kept in shared scope to avoid cross-layer imports. */
export type TitleStatus = "pending" | "approved" | "suspended" | "rejected" | "editing";

interface TitleActionsCellProps {
  status: TitleStatus;
  onView?: () => void;
  onApprove?: () => void;
  onReject?: () => void;
  onSuspend?: () => void;
  onReactivate?: () => void;
  onDelete?: () => void;
  /** Billing on/off state. When defined (+ status approved), renders an active/inactive toggle. */
  billingActive?: boolean;
  onActivate?: () => void;
  onDeactivate?: () => void;
}

/**
 * Title workflow action cell — 5-state machine.
 *
 *   pending   → [👁 view] [✓ approve] [✗ reject]
 *   approved  → [👁 view] [⏻ activate / ⏼ deactivate] [⏸ suspend]
 *   suspended → [👁 view] [↺ reactivate] [🗑 delete]
 *   rejected  → [👁 view] [🗑 delete]
 *   editing   → [👁 view] [✓ approve]
 *
 * The billing toggle (activate/deactivate) is orthogonal to the lifecycle status
 * and only shows for APPROVED titles when `billingActive` is provided.
 * Stops click propagation so row click doesn't fire.
 */
export function TitleActionsCell({
  status,
  onView,
  onApprove,
  onReject,
  onSuspend,
  onReactivate,
  onDelete,
  billingActive,
  onActivate,
  onDeactivate,
}: TitleActionsCellProps) {
  const { t } = useTranslation("admin");

  return (
    <div
      className="flex items-center gap-1"
      onClick={(e) => e.stopPropagation()}
    >
      {onView && (
        <RowActionButton
          action="view"
          onClick={onView}
          aria-label={t("titles.actions.view")}
          title={t("titles.actions.view")}
        />
      )}

      {/* approved: billing on/off toggle (orthogonal to suspend) */}
      {status === "approved" && billingActive !== undefined && (
        billingActive ? (
          <RowActionButton
            action="deactivate"
            onClick={onDeactivate}
            aria-label={t("titles.actions.deactivate")}
            title={t("titles.actions.deactivate")}
          />
        ) : (
          <RowActionButton
            action="activate"
            onClick={onActivate}
            aria-label={t("titles.actions.activate")}
            title={t("titles.actions.activate")}
          />
        )
      )}

      {/* pending: approve + reject + delete */}
      {status === "pending" && (
        <>
          <RowActionButton
            action="approve"
            onClick={onApprove}
            aria-label={t("titles.actions.approve")}
            title={t("titles.actions.approve")}
          />
          <RowActionButton
            action="reject"
            onClick={onReject}
            aria-label={t("titles.actions.reject")}
            title={t("titles.actions.reject")}
          />
          <RowActionButton
            action="delete"
            onClick={onDelete}
            aria-label={t("titles.actions.delete")}
            title={t("titles.actions.delete")}
          />
        </>
      )}

      {/* approved: suspend */}
      {status === "approved" && (
        <RowActionButton
          action="suspend"
          onClick={onSuspend}
          aria-label={t("titles.actions.suspend")}
          title={t("titles.actions.suspend")}
        />
      )}

      {/* suspended: reactivate */}
      {status === "suspended" && (
        <RowActionButton
          action="reactivate"
          onClick={onReactivate}
          aria-label={t("titles.actions.reactivate")}
          title={t("titles.actions.reactivate")}
        />
      )}

      {/* editing: approve */}
      {status === "editing" && (
        <RowActionButton
          action="approve"
          onClick={onApprove}
          aria-label={t("titles.actions.approve")}
          title={t("titles.actions.approve")}
        />
      )}
    </div>
  );
}